<template>
  <div class="w-full mx-auto px-4 py-6">
    <!-- Toolbar Section -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <h1 class="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Request links</h1>
        <span
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200"
          data-testid="request-contents-items-count"
        >
          {{ totalItems }}
        </span>
        <Button
          type="link"
          variant="gray"
          icon="general.cog"
          tooltip="Request video settings"
          @click="navigateToSettings"
          data-testid="request-contents-settings-button"
        />
      </div>
      <div class="flex items-center gap-3">
        <AddRequestContentButton :disabled="isLoading" />
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error && !isLoading" class="error-container">
      <h3 class="error-title">Failed to load request links</h3>
      <p class="error-message">{{ error }}</p>
      <Button type="solid" variant="blue" text="Try Again" @click="loadRequestContentsWithCollections" />
    </div>

    <!-- Table Section with integrated pagination -->
    <div v-else class="mb-6">
      <RequestContentList
        :list="requestContents"
        :domain="domain"
        :isLoading="isLoading"
        :current-page="meta.currentPage"
        :total-pages="meta.totalPages"
        :total-items="totalItems"
        :per-page="meta.perPage"
        @edit="handleEdit"
        @delete="handleDelete"
        @page-change="changePage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Button } from '@/storybook';
import { useToast } from '@/composables/useToast';
import { useConfirmModal } from '@/storybook/modal/useModal';
import type { RequestContent } from './types';
import { useRequestContents } from './composables/useRequestContents';
import { useRequestContentModals } from './composables/useRequestContentModals';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import AddRequestContentButton from './components/shared/AddRequestContentButton.vue';
import RequestContentList from './components/list/RequestContentList.vue';

// Router
const route = useRoute();
const router = useRouter();

// Stores
const requestContentStore = useRequestContentStore();
const organizationStore = useOrganizationStore();
const { meta, domain, isLoading, error, totalItems } = storeToRefs(requestContentStore);

// Composables - only for data transformation
const { requestContents, loadRequestContentsWithCollections, changePage } = useRequestContents();

const { showAddModal, showEditModal } = useRequestContentModals();
const { success, error: showError } = useToast();

// Confirm modal for delete actions
const { open: openDeleteConfirm } = useConfirmModal({
  title: 'Delete request link?',
  message: 'This cannot be undone.',
  confirmText: 'Delete',
  cancelText: 'Cancel',
  dataTestid: 'confirm-modal',
});

const handleEdit = (item: RequestContent) => {
  return showEditModal({
    item,
    domain: domain.value,
    onSave: async () => {
      // The API call is handled inside the modal
      // Just refresh the list to get the latest data
      await loadRequestContentsWithCollections();
    },
  });
};

const handleDelete = async (item: RequestContent) => {
  const confirmed = await openDeleteConfirm();
  if (confirmed) {
    try {
      await requestContentStore.deleteRequestContent(item);
      success('Request link deleted successfully');
    } catch (error) {
      console.error('Failed to delete request content:', error);
      showError('Failed to delete request link. Please try again.');
    }
  }
};

const navigateToSettings = () => {
  const orgId = organizationStore.id;
  if (orgId) {
    window.location.href = `/organizations/${orgId}/settings/request`;
  }
};

// Auto-open create modal via ?create=true (e.g. from legacy dashboard button)
const openCreateModalFromQuery = () => {
  if (route.query.create === 'true') {
    router.replace({ query: { ...route.query, create: undefined } });
    showAddModal({
      domain: domain.value,
      onSave: async () => {
        await loadRequestContentsWithCollections();
      },
    });
  }
};

// Lifecycle
onMounted(async () => {
  await loadRequestContentsWithCollections();
  openCreateModalFromQuery();
});
</script>

<style scoped>
/* Error States */
.error-container {
  @apply flex flex-col items-center justify-center py-16 px-4 text-center;
}

.error-title {
  @apply text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2;
}

.error-message {
  @apply text-zinc-600 dark:text-zinc-400 mb-6 max-w-md;
}
</style>
